import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import { Observable, Subject} from 'rxjs';
import { TemplateGroup as Entity} from './template-group.model';
import {TemplateGroupActions as Actions} from './template-group.actions';
import {TemplateGroupSelectors as Selectors} from "./template-group.selectors";
import {AppState} from "src/app/store/app-store.state";
import {generateUniqueId} from "src/app/store/app-store.functions";
import {PaginatedResult} from "src/app/store/app-store-entity.service";
import {filter, first, map} from "rxjs/operators";
import { Criteria } from "src/app/store/app-store.types";
import {IPage, ITableSort} from "src/app/store/app-store.interfaces";
import {TemplateGroupService} from "./template-group.service";

@Injectable({providedIn: 'root'})
export class TemplateGroupFacade {
  all$: Observable<Entity[]> = this.store.pipe(select(Selectors.selectAll));
  isLoading$: Observable<boolean> = this.store.pipe(select(Selectors.isLoading));
  hasError$: Observable<Error | undefined> = this.store.pipe(select(Selectors.hasErrors));
  current$: Observable<Entity | undefined> = this.store.pipe(select(Selectors.currentEntity));
  currentId$: Observable<number | undefined> = this.store.pipe(select(Selectors.currentEntityId));
  totalPageable$: Observable<number> = this.store.pipe(select(Selectors.totalPageable));
  currentPage$: Observable<IPage> = this.store.pipe(select(Selectors.currentPage));
  currentSort$: Observable<ITableSort | undefined> = this.store.pipe(select(Selectors.currentSort));

  private _refreshPage = new Subject<void>();
  refreshPage$: Observable<void> = this._refreshPage.asObservable();

  constructor(
    private store: Store<AppState>,
    private templateGroupService: TemplateGroupService,
  ) {

  }

  deselect(): void {
    this.store.dispatch(Actions.deselectEntities());
  }

  refreshPage(): void {
    this._refreshPage.next();
  }

  setPaginationSettings(page: IPage): void {
    this.store.dispatch(Actions.setPaginationSettings({page}));
    this.refreshPage();
  }

  setSortSettings(tableSort: ITableSort): void {
    this.store.dispatch(Actions.setSortSettings({tableSort}));
  }

  setCurrentId(entityId: number): void {
    this.store.dispatch(Actions.setCurrentEntityId({entityId}));
  }

  loadPage(pageNumber: number, pageSize: number, criteria: Criteria = undefined): void {
    this.store.dispatch(Actions.loadPage({criteria, page: {pageNumber, pageSize}}));
  }

  loadPageWithResult(pageNumber: number, pageSize: number, criteria: Criteria = undefined): Observable<PaginatedResult<Entity>> {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.loadPage({page: {pageNumber, pageSize}, criteria, correlationId: correlationId}));
    return this.store.select(Selectors.findByCorrelationId<PaginatedResult<Entity>>(correlationId))
      .pipe(filter((x) => !!x));
  }

  load(criteria: Criteria = undefined): void {
    this.store.dispatch(Actions.load({criteria}));
  }

  loadWithResult(criteria: Criteria = undefined): Observable<Entity[]> {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.load({criteria, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity[]>(correlationId))
      .pipe(filter((x) => !!x));
  }

  loadSingleWithResult(entityId: number): Observable<Entity> {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.loadSingleIfNecessary({entityId, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x), first());
  }


  create(entity: Entity): void {
    this.store.dispatch(Actions.create({entity}));
  }

  createWithResult(entity: Entity): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.create({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  createManyWithResult(entities: Entity[]): Observable<Entity[]> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.createMany({entities, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity[]>(correlationId))
      .pipe(filter((x) => !!x));
  }

  update(entity: Entity): void {
    this.store.dispatch(Actions.update({entity, correlationId: generateUniqueId()}));
  }

  updateWithResult(entity: Entity): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.update({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  updateMany(entities: Entity[]): void {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.updateMany({entities, correlationId: correlationId}));
  }

  updateManyWithResult(entities: Entity[]): Observable<Entity[]> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.updateMany({entities, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity[]>(correlationId))
      .pipe(filter((x) => !!x));
  }

  remove(entityId: number): void {
    this.store.dispatch(Actions.remove({entityId}));
  }

  removeWithResult(entityId: number): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.remove({entityId, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity | Error>(correlationId))
      .pipe(
        filter((x) => !!x),
        first(),
        map((result: any) => {
          if (result instanceof Error) throw result;
          if (result && typeof result === 'object' && 'status' in result && 'statusText' in result) {
            throw new Error(result.error?.message ?? result.error ?? result.statusText ?? 'Operation failed');
          }
          return result as Entity;
        })
      );
  }

  removeMany(entityIds: number[]): void {
    this.store.dispatch(Actions.removeMany({entityIds}));
  }

  removeManyWithResult(entityIds: number[]): Observable<number[]> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.removeMany({entityIds, correlationId}));

    return this.store.select(Selectors.findByCorrelationId<number[] | Error>(correlationId))
      .pipe(
        filter((x) => !!x),
        first(),
        map((result: any) => {
          if (result instanceof Error) throw result;
          if (result && typeof result === 'object' && 'status' in result && 'statusText' in result) {
            throw new Error(result.error?.message ?? result.error ?? result.statusText ?? 'Operation failed');
          }
          return result as number[];
        })
      );
  }

  clone(entity: Entity): void {
    this.store.dispatch(Actions.clone({entity}));
  }

  cloneWithResult(entity: Entity): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.clone({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  getById(entityId: number): Observable<Entity | undefined> {
    return this.store.pipe(select(Selectors.selectById(entityId)));
  }

  addToCategories(templateGroupIds: number[], categoryIds: number[]): Observable<boolean> {
    return this.templateGroupService.addToCategories(templateGroupIds, categoryIds);
  }

  cloneToCategories(templateGroupIds: number[], categoryIds: number[], name?: string): Observable<boolean> {
    return this.templateGroupService.cloneToCategories(templateGroupIds, categoryIds, name);
  }

  moveToCategories(templateGroupIds: number[], categoryIds: number[]): Observable<boolean> {
    return this.templateGroupService.moveToCategories(templateGroupIds, categoryIds);
  }

  removeFromCategory(templateGroupId: number, categoryId: number): Observable<boolean> {
    return this.templateGroupService.removeFromCategory(templateGroupId, categoryId);
  }
}
